import defaultHttpPlatform from '@/core/api/httpPlatform'
import defaultReceiptScansApi from '@/core/api/receiptScans'

// Backoff was [1000, 2000, 3000] — that capped detection latency at 3s, which the
// user perceived as the "Scanning your receipt" progress card lingering for ~1s on
// average after Photon actually finished. Halved across the board: first poll 500ms,
// then 750ms, then 1000ms thereafter. Worst-case gap drops from 3s to 1s; average
// drops from ~1.5s to ~500ms — at the cost of ~2x as many polls per scan (~12-13
// over a 15s scan instead of ~7), which Platform's getStatus easily handles.
const BACKOFF_SCHEDULE = [500, 750, 1000]
const POLL_TIMEOUT_MS = 60000
const SCANNING_LONG_THRESHOLD_MS = 15000

const STATUS_UPLOADING = 'Uploading'
const STATUS_SCANNING_SHORT = 'ScanningShort'
const STATUS_SCANNING_LONG = 'ScanningLong'
const STATUS_POLLING_TIMEOUT = 'PollingTimeout'
const STATUS_COMPLETE = 'Complete'
const STATUS_FAILED = 'Failed'
const STATUS_PLATFORM_UNREACHABLE = 'PlatformUnreachable'

function createReceiptScanPoller (options) {
  var receiptScanId = options.receiptScanId
  var contextHint = options.contextHint || {}
  var onStateChange = options.onStateChange || function () {}
  var onDone = options.onDone || function () {}
  var onError = options.onError || function () {}

  var httpPlatform = options.httpPlatform || defaultHttpPlatform
  var receiptScansApi = options.receiptScansApi || defaultReceiptScansApi

  var pollIndex = 0
  var timerId = null
  var timeoutId = null
  var cancelled = false
  var running = false
  var startedAt = null
  // Per-cycle controller so cleanup() can abort the in-flight axios request
  // (otherwise the pending getStatus resolves through httpPlatform's 3-retry
  // backoff before noticing it was cancelled).
  var controller = null
  // Extra retry budget for transient 5xx/network errors that get past the
  // httpPlatform retry interceptor (3 attempts). Capped low so we don't
  // compound budgets.
  var transientRetryCount = 0
  var TRANSIENT_RETRY_CAP = 2
  var DEFAULT_429_RETRY_MS = 5000

  function getDelay () {
    var idx = pollIndex < BACKOFF_SCHEDULE.length ? pollIndex : BACKOFF_SCHEDULE.length - 1
    return BACKOFF_SCHEDULE[idx]
  }

  function resolveStatus (serverStatus, elapsed) {
    if (serverStatus === 'ReadyForMatch' || serverStatus === 'Matched' || serverStatus === 'FolderSaved') {
      return STATUS_COMPLETE
    }
    if (serverStatus === 'Failed' || serverStatus === 'Cancelled') {
      return STATUS_FAILED
    }
    if (serverStatus === 'Uploaded' || serverStatus === 'Scanning') {
      return elapsed >= SCANNING_LONG_THRESHOLD_MS ? STATUS_SCANNING_LONG : STATUS_SCANNING_SHORT
    }
    return STATUS_UPLOADING
  }

  function poll () {
    if (cancelled) {
      return
    }

    var elapsed = Date.now() - startedAt

    if (elapsed >= POLL_TIMEOUT_MS) {
      onStateChange(STATUS_POLLING_TIMEOUT)
      onError({ type: 'timeout', message: 'Polling timed out after ' + POLL_TIMEOUT_MS + 'ms' })
      cleanup()
      return
    }

    controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    var requestConfig = controller ? { signal: controller.signal } : undefined

    receiptScansApi.getStatus(receiptScanId, requestConfig)
      .then(function (data) {
        if (cancelled) {
          return
        }

        // Reset transient budget on any successful response.
        transientRetryCount = 0

        var status = resolveStatus(data.status, elapsed)

        onStateChange(status, data)

        if (status === STATUS_COMPLETE) {
          onDone({
            receiptScanId: receiptScanId,
            status: data.status,
            autoLinked: data.autoLinked || false,
            matchCandidates: data.matchCandidates || [],
            autoFill: data.autoFill || null,
            warnings: data.warnings || [],
            contextHint: contextHint,
            // Full getStatus response — lets consumers skip an extra round-trip when
            // they need fields not in the curated payload above (e.g. rowVersion,
            // merchant, transactionDate, amount). Non-breaking addition: prior
            // consumers that destructure the curated keys continue to work.
            data: data
          })
          cleanup()
          return
        }

        if (status === STATUS_FAILED) {
          onError({ type: 'scan_failed', message: 'Receipt scan failed', data: data })
          cleanup()
          return
        }

        pollIndex++
        timerId = setTimeout(poll, getDelay())
      })
      .catch(function (error) {
        if (cancelled) {
          return
        }

        // 401 — token rotation / auth failure. Signal caller and stop; the poller
        // doesn't try to re-auth.
        if (error.response && error.response.status === 401) {
          onError({ type: 'auth_failure', message: 'Authentication failed', error: error })
          cleanup()
          return
        }

        // 429 — respect Retry-After if present, otherwise back off ~5s. Keep polling.
        if (error.response && error.response.status === 429) {
          var retryAfterHeader = error.response.headers &&
            (error.response.headers['retry-after'] || error.response.headers['Retry-After'])
          var retryAfterMs = DEFAULT_429_RETRY_MS
          if (retryAfterHeader) {
            var parsed = parseInt(retryAfterHeader, 10)
            if (!isNaN(parsed) && parsed > 0) {
              retryAfterMs = parsed * 1000
            }
          }
          timerId = setTimeout(poll, retryAfterMs)
          return
        }

        // 5xx / network blip — httpPlatform's retry interceptor already attempted
        // 3 retries with backoff. Give the poller one or two extra attempts before
        // surfacing as unreachable.
        if (httpPlatform.isPlatformSentinelError(error)) {
          if (transientRetryCount < TRANSIENT_RETRY_CAP) {
            transientRetryCount++
            timerId = setTimeout(poll, getDelay())
            return
          }
          onStateChange(STATUS_PLATFORM_UNREACHABLE)
          onError({ type: 'unreachable', message: 'Platform unreachable', error: error })
          cleanup()
          return
        }

        // Other 4xx / structural — tear down.
        onError({ type: 'poll_error', message: 'Polling error', error: error })
        cleanup()
      })
  }

  function cleanup () {
    running = false
    if (controller) {
      try { controller.abort() } catch (e) { /* AbortController.abort never throws in normal browsers; defensive */ }
      controller = null
    }
    if (timerId) {
      clearTimeout(timerId)
      timerId = null
    }
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return {
    start: function () {
      if (running) {
        return
      }
      running = true
      cancelled = false
      pollIndex = 0
      startedAt = Date.now()
      poll()
    },

    cancel: function () {
      cancelled = true
      cleanup()
    }
  }
}

createReceiptScanPoller.STATUS_UPLOADING = STATUS_UPLOADING
createReceiptScanPoller.STATUS_SCANNING_SHORT = STATUS_SCANNING_SHORT
createReceiptScanPoller.STATUS_SCANNING_LONG = STATUS_SCANNING_LONG
createReceiptScanPoller.STATUS_POLLING_TIMEOUT = STATUS_POLLING_TIMEOUT
createReceiptScanPoller.STATUS_COMPLETE = STATUS_COMPLETE
createReceiptScanPoller.STATUS_FAILED = STATUS_FAILED
createReceiptScanPoller.STATUS_PLATFORM_UNREACHABLE = STATUS_PLATFORM_UNREACHABLE

export default createReceiptScanPoller



// WEBPACK FOOTER //
// ./src/core/receiptScan/receiptScanPoller.js